import React, { useState } from 'react';
import { QrCode, Check, AlertCircle, Copy, Share2 } from 'lucide-react';
import PixQRModal from '../components/pix/PixQRModal';

const PixTestPage: React.FC = () => {
  const [showPixQR, setShowPixQR] = useState(false);
  const [testData, setTestData] = useState({
    pixKey: '11999887766',
    pixKeyType: 'phone' as 'cpf' | 'cnpj' | 'email' | 'phone',
    pixHolderName: 'João da Silva',
    amount: 50.00,
    groupName: 'Time de Teste'
  });

  const testCases = [
    {
      name: 'Telefone',
      pixKey: '11999887766',
      pixKeyType: 'phone' as const,
      pixHolderName: 'João da Silva',
      amount: 50.00,
      expected: '+5511999887766'
    },
    {
      name: 'CPF',
      pixKey: '123.456.789-00',
      pixKeyType: 'cpf' as const,
      pixHolderName: 'Maria Santos',
      amount: 80.00,
      expected: '12345678900'
    },
    {
      name: 'E-mail',
      pixKey: 'joao@exemplo.com',
      pixKeyType: 'email' as const,
      pixHolderName: 'João Silva',
      amount: 100.00,
      expected: 'joao@exemplo.com'
    },
    {
      name: 'CNPJ',
      pixKey: '12.345.678/0001-90',
      pixKeyType: 'cnpj' as const,
      pixHolderName: 'Empresa LTDA',
      amount: 200.00,
      expected: '12345678000190'
    }
  ];

  const handleTestCase = (testCase: typeof testCases[0]) => {
    setTestData({
      pixKey: testCase.pixKey,
      pixKeyType: testCase.pixKeyType,
      pixHolderName: testCase.pixHolderName,
      amount: testCase.amount,
      groupName: 'Time de Teste'
    });
    setShowPixQR(true);
  };

  const validatePixKey = (key: string, type: string) => {
    switch (type) {
      case 'cpf':
        const cpfDigits = key.replace(/\D/g, '');
        return cpfDigits.length === 11;
      case 'cnpj':
        const cnpjDigits = key.replace(/\D/g, '');
        return cnpjDigits.length === 14;
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(key);
      case 'phone':
        const phoneDigits = key.replace(/\D/g, '');
        return phoneDigits.length >= 10 && phoneDigits.length <= 11;
      default:
        return false;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Teste de QR Code PIX</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Teste a geração de QR Codes PIX com diferentes tipos de chaves
        </p>
      </header>

      {/* Teste Manual */}
      <div className="card p-6 mb-8">
        <h2 className="text-lg font-bold mb-4">Teste Manual</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">
              Tipo de Chave PIX
            </label>
            <select
              className="input w-full"
              value={testData.pixKeyType}
              onChange={(e) => setTestData(prev => ({ 
                ...prev, 
                pixKeyType: e.target.value as any 
              }))}
            >
              <option value="phone">Telefone</option>
              <option value="cpf">CPF</option>
              <option value="email">E-mail</option>
              <option value="cnpj">CNPJ</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Chave PIX
            </label>
            <input
              type="text"
              className="input w-full"
              value={testData.pixKey}
              onChange={(e) => setTestData(prev => ({ 
                ...prev, 
                pixKey: e.target.value 
              }))}
              placeholder={
                testData.pixKeyType === 'cpf' ? '000.000.000-00' :
                testData.pixKeyType === 'cnpj' ? '00.000.000/0000-00' :
                testData.pixKeyType === 'email' ? 'email@exemplo.com' :
                '(11) 99999-9999'
              }
            />
            <div className="mt-1 flex items-center gap-2">
              {validatePixKey(testData.pixKey, testData.pixKeyType) ? (
                <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <Check className="w-4 h-4" />
                  <span className="text-sm">Chave válida</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Chave inválida</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Nome do Titular
            </label>
            <input
              type="text"
              className="input w-full"
              value={testData.pixHolderName}
              onChange={(e) => setTestData(prev => ({ 
                ...prev, 
                pixHolderName: e.target.value 
              }))}
              placeholder="Nome completo do titular"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Valor (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              className="input w-full"
              value={testData.amount}
              onChange={(e) => setTestData(prev => ({ 
                ...prev, 
                amount: parseFloat(e.target.value) || 0 
              }))}
            />
          </div>
        </div>

        <button
          onClick={() => setShowPixQR(true)}
          className="btn-primary w-full"
          disabled={!validatePixKey(testData.pixKey, testData.pixKeyType) || !testData.pixHolderName || testData.amount <= 0}
        >
          <QrCode className="w-4 h-4 mr-2" />
          Gerar QR Code PIX
        </button>
      </div>

      {/* Casos de Teste Pré-definidos */}
      <div className="card p-6">
        <h2 className="text-lg font-bold mb-4">Casos de Teste Pré-definidos</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Teste com diferentes tipos de chaves PIX para validar a geração do QR Code
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testCases.map((testCase, index) => (
            <div key={index} className="p-4 border dark:border-dark-border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">{testCase.name}</h3>
                <span className="text-sm px-2 py-1 rounded-full bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400">
                  {testCase.pixKeyType.toUpperCase()}
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-500">Chave:</span>
                  <span className="ml-2 font-mono">{testCase.pixKey}</span>
                </div>
                <div>
                  <span className="text-gray-500">Titular:</span>
                  <span className="ml-2">{testCase.pixHolderName}</span>
                </div>
                <div>
                  <span className="text-gray-500">Valor:</span>
                  <span className="ml-2 font-medium">
                    {testCase.amount.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Formato EMV:</span>
                  <span className="ml-2 font-mono text-xs">{testCase.expected}</span>
                </div>
              </div>

              <button
                onClick={() => handleTestCase(testCase)}
                className="btn-primary w-full mt-4"
              >
                Testar {testCase.name}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Informações Técnicas */}
      <div className="card p-6 mt-6">
        <h2 className="text-lg font-bold mb-4">Informações Técnicas</h2>
        
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="font-medium text-blue-600 dark:text-blue-400 mb-2">
              Formato EMV PIX
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              O QR Code PIX usa o padrão EMV (Europay, Mastercard, Visa) definido pelo Banco Central do Brasil.
              Cada campo tem um ID, tamanho e valor específicos.
            </p>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <h3 className="font-medium text-green-600 dark:text-green-400 mb-2">
              Validação nos Bancos
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Os aplicativos bancários validam o CRC16, formato da chave PIX e estrutura EMV.
              Qualquer erro nestes campos torna o QR Code inválido.
            </p>
          </div>

          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <h3 className="font-medium text-yellow-600 dark:text-yellow-400 mb-2">
              Formatação de Chaves
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
              <li><strong>Telefone:</strong> +55DDNNNNNNNNN (formato E.164)</li>
              <li><strong>CPF:</strong> Apenas dígitos (11 caracteres)</li>
              <li><strong>CNPJ:</strong> Apenas dígitos (14 caracteres)</li>
              <li><strong>E-mail:</strong> Formato padrão de e-mail</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modal PIX QR Code */}
      {showPixQR && (
        <PixQRModal
          isOpen={showPixQR}
          onClose={() => setShowPixQR(false)}
          pixKey={testData.pixKey}
          pixKeyType={testData.pixKeyType}
          pixHolderName={testData.pixHolderName}
          amount={testData.amount}
          groupName={testData.groupName}
        />
      )}
    </div>
  );
};

export default PixTestPage;